<template>
  <v-form ref="form" @input="onChange">
    <v-row>
      <v-col cols="12">
        <h3 class="mb-4">Podaci o blagajni</h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            autofocus
            dense
            outlined
            label="Naziv blagajne"
            v-model="cashRegister.name"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Naziv blagajne"
            description="Jedinstveni naziv koji će identificirati ovu blagajnu u sustavu. Korisno ako imate više blagajni na istom prodajnom mjestu."
            :required="true"
            example="Blagajna 1, Centralna blagajna, Kasa A"
            notes="Naziv bude vidljiv u aplikaciji i pomoći će vam razlikovati različite blagajne. Može biti jednostavan i deskriptivan."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            ref="numberField"
            dense
            outlined
            label="Oznaka blagajne"
            v-model="cashRegister.number"
            v-mask="'###########'"
            :rules="[rules.req]"
            hint="Samo brojevi (max 11 znamenki)"
            persistent-hint
          />
          <field-help-dialog
            title="Oznaka blagajne"
            description="Numerička oznaka (broj) blagajne koja se koristi za identifikaciju u sustavu fiskalizacije. Svaka blagajna mora imati jedinstveni broj."
            :required="true"
            format="Samo brojevi, maksimalno 11 znamenki"
            example="1, 2, 12345"
            notes="Ova oznaka se koristi u JIR-u (Jedinstveni identifikator računa) i mora biti jedinstvena za sve vaše blagajne. Nakon postavljanja, ne možete je mijenjati. Obično se koriste jednostavni brojevi poput 1, 2, 3 itd."
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-select
            dense
            outlined
            label="Vrsta blagajne"
            v-model="cashRegister.type"
            :items="availableRegisterTypes"
            item-text="name"
            item-value="value"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Vrsta blagajne"
            description="Tip blagajne određuje koje vrste računa možete izdavati i da li će računi biti fiskalizirani putem Porezne uprave."
            :required="true"
            notes="TRANSAKCIJSKA (VP): Za izdavanje računovodstvenih računa koji se standardno ne šalju Poreznoj upravi (B2B, avansni računi, predračuni). Ne zahtijeva certifikat, osim za obveznike fiskalizacije F2 koji moraju fiskalizirati i transakcijske račune.

GOTOVINSKA (MP): Za fiskalizirane račune za gotovinsko plaćanje koji se obavezno šalju Poreznoj upravi. Zahtijeva certifikat.

MJEŠOVITA (ALL): Omogućuje izdavanje i gotovinskih i transakcijskih računa. Zahtijeva certifikat.

NAPOMENA: Gotovinska i Mješovita blagajna dostupne su samo ako ste učitali certifikat u prethodnom koraku. Vrstu blagajne možete naknadno promijeniti u postavkama nakon što učitate certifikat."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-select
            dense
            outlined
            label="Printer"
            v-model="cashRegister.printer"
            :items="printers"
            item-text="name"
            item-value="id"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Tip printera"
            description="Tip printera koji će se koristiti za ispis računa sa ove blagajne."
            :required="true"
            notes="Klasični printer A4 je standardni uredski printer koji ispisuje račune i fakture na A4 papir u punoj veličini. Idealan je za detaljne ispise dokumenata s svim potrebnim informacijama.

Trenutno je ovo jedina dostupna opcija u wizardu za brzo postavljanje. Nakon završetka postavljanja, moguće je dodati i druge vrste printera (POS printer 58mm, POS printer 80mm) kroz postavke blagajne."
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-select
            dense
            outlined
            label="Vrsta prijave na blagajnu"
            v-model="cashRegister.login_type"
            :items="registerLoginTypes"
            item-text="name"
            item-value="value"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Vrsta prijave na blagajnu"
            description="Način na koji će se korisnici prijavljivati na blagajnu pri početku rada."
            :required="true"
            notes="AUTOMATSKA PRIJAVA: Blagajna se automatski otvara bez potrebe za prijavom - praktično ako samo jedna osoba koristi blagajnu.

IZBOR KORISNIKA: Prilikom otvaranja blagajne odabire se korisnik sa liste - dobro za više prodavača koji rade s različitih uređaja.

PRIJAVA S PIN KODOM: Svaki prodavač mora unijeti svoj PIN kod - najviša razina sigurnosti, preporuka za više prodavača na jednom uređaju.

PREPORUKA: Za samozaposlene koristite automatsku prijavu. Za više prodavača koristite PIN kod."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-select
            dense
            outlined
            label="Nakon izdavanja računa"
            v-model="cashRegister.after_receipt_behaviour"
            :items="afterReceiptBehaviours"
            item-text="name"
            item-value="value"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Ponašanje nakon izdavanja računa"
            description="Što će se prikazati u aplikaciji nakon što završite izdavanje računa."
            :required="true"
            notes="OČISTI KOŠARICU: Vraća se na praznu košaricu spremnu za novi unos - najbrža opcija za kontinuiranu prodaju.

PRIKAŽI SVE BLAGAJNE: Prikazuje pregled svih blagajni - korisno ako redovno prebacujete između blagajni.

PRIKAŽI SVE RAČUNE: Prikazuje listu svih izdanih računa - korisno za pregled i provjeru nedavno izdanih računa.

PREPORUKA: Za brzu prodaju koristite 'Očisti košaricu'. Za kontrolu i uvid koristite 'Prikaži sve račune'."
          />
        </div>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import rules from '@/plugins/rules'
import FieldHelpDialog from '../shared/FieldHelpDialog.vue'

export default {
  name: 'StepCashRegister',
  components: {
    FieldHelpDialog
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    companyData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      cashRegister: {
        name: 'Blagajna',
        number: '',
        type: 'VP',
        printer: 'A4',
        login_type: 'DEFAULT',
        after_receipt_behaviour: 'EMPTY_BASKET'
      },
      registerTypes: [
        { name: 'Gotovinska', value: 'MP' },
        { name: 'Transakcijska', value: 'VP' },
        { name: 'Mješovita', value: 'ALL' }
      ],
      registerLoginTypes: [
        { name: 'Automatska prijava', value: 'DEFAULT' },
        { name: 'Izbor korisnika', value: 'SELECT_USER' },
        { name: 'Prijava s PIN kodom', value: 'PINCODE' }
      ],
      afterReceiptBehaviours: [
        { name: 'Očisti košaricu', value: 'EMPTY_BASKET' },
        { name: 'Prikaži sve blagajne', value: 'SHOW_CASH_REGS' },
        { name: 'Prikaži sve račune', value: 'SHOW_ALL_RECEIPTS' }
      ],
      printers: [
        { name: 'Klasični printer A4', id: 'A4' }
      ],
      rules: {
        req: rules.req()
      }
    }
  },
  computed: {
    availableRegisterTypes () {
      // Ako firma nema certifikat, filtriraj MP i ALL tipove
      const hasCertificate = this.companyData.certificate && this.companyData.certificate.trim() !== ''

      if (!hasCertificate) {
        return this.registerTypes.filter(type => type.value !== 'MP' && type.value !== 'ALL')
      }

      return this.registerTypes
    }
  },
  watch: {
    cashRegister: {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    availableRegisterTypes (types) {
      const mp = types.find(t => t.value === 'MP')
      this.cashRegister.type = mp ? 'MP' : 'VP'
    }
  },
  methods: {
    validate () {
      return this.$refs.form.validate()
    },
    focusNumber () {
      this.$nextTick(() => {
        if (this.$refs.numberField) this.$refs.numberField.focus()
      })
    },
    onChange () {
      this.$emit('change')
    },
    getData () {
      return this.cashRegister
    },
    resetToDefaults () {
      const hasCertificate = this.companyData.certificate && this.companyData.certificate.trim() !== ''
      this.cashRegister = {
        name: this.cashRegister.name || 'Blagajna',
        number: this.cashRegister.number || '',
        type: hasCertificate ? 'MP' : 'VP',
        printer: 'A4',
        login_type: 'DEFAULT',
        after_receipt_behaviour: 'EMPTY_BASKET'
      }
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.validate()
      })
    }
  },
  mounted () {
    if (this.value && Object.keys(this.value).length > 0) {
      this.cashRegister = { ...this.cashRegister, ...this.value }
    }
    this.resetToDefaults()
  }
}
</script>

<style scoped>
h3 {
  color: #1577da;
}
</style>
